import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  input,
  ElementRef,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core'
import { MatIcon } from '@angular/material/icon'

@Component({
  selector: 'app-collapsible-section',
  imports: [CommonModule, MatIcon],
  styles: `
    :host {
      display: block;
      position: relative;

      .collapsible-content {
        min-height: 0;
        max-height: 0;
        visibility: hidden;
        padding-right: 3rem;
        padding-bottom: 0;
        padding-left: 3rem;
        overflow-y: hidden;

        &.expanded {
          visibility: visible;
          max-height: unset;
          overflow-y: unset;
          padding-bottom: 0.5rem;
        }
      }
      
      .collapsible-control {
        display: none;
        
        &.overflow-when-collapsed {
          display: flex;
          justify-content: center;
          background: linear-gradient(0deg, #eee, white 40%, white 0.9rem, transparent 100%);
        }
        
        button {
          position: relative;
          top: -0.5rem;
          border: none;
          outline: none;
          font-size: 0.9rem;
          height: 1rem;
          line-height: 1rem;
          background: none;
          visibility: hidden;

          &:enabled {
            visibility: visible;
            &:hover {
              color: var(--active-blue);
            }
          }

          .label {
            position: relative;
            top: -6px;
          }
        }
      }
    }
  `,
  template: `
    <div
        #section
        class="collapsible-content"
        [class.expanded]="expanded() && overflowWhenCollapsed()"
    >
      <ng-content/>
    </div>
    <div class="collapsible-control" [class.overflow-when-collapsed]="overflowWhenCollapsed()">
      <button [disabled]="collapseDisabled()" (click)="onExpandCollapse()">
        <span class='label'>{{ buttonText() }}</span>
        <mat-icon>{{ iconName() }}</mat-icon>
      </button>
    </div>
  `,
})
export class CollapsibleSectionComponent implements AfterViewInit, OnDestroy {
  readonly collapseDisabled = input(false)

  protected readonly expanded = signal(true)
  protected readonly overflowWhenCollapsed = signal(false)

  protected readonly buttonText = computed((): string =>
    this.expanded() ? 'Collapse' : 'Expand',
  )

  protected readonly iconName = computed((): string =>
    this.expanded() ? 'keyboard_arrow_up' : 'keyboard_arrow_down',
  )

  private readonly resizeObserver = new ResizeObserver(([entry]) => {
    this.overflowWhenCollapsed.set(entry.target.scrollHeight > 48)
  })

  private collapsibleSection = viewChild<ElementRef<HTMLDivElement>>('section')

  constructor() {
    effect(() => {
      if (this.collapseDisabled() && !this.expanded()) {
        this.expanded.set(true)
      }
    })
  }

  ngAfterViewInit() {
    if (this.collapsibleSection()) {
      this.resizeObserver.observe(this.collapsibleSection()!.nativeElement)
    }    
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect()
  }

  protected onExpandCollapse() {
    this.expanded.update(expanded => !expanded)
  }
}
